import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs/Observable';
import { PtLuxonDate } from '../../models/projects/date.models';
import { JackingActivityEditor, JackingActivityTypes } from '../../models/projects/jacking-activity.models';
import { ErrorType, ValidationResult } from '../../models/projects/validation.models';
import { VesselClasses } from '../../models/setup/setup.models';
import { VesselService } from '../../services/vessel/vessel.service';
import { JackingActivityService } from './jacking-activity.service';
import { TimeZoneChangeEditor } from '../../models/time-zones/time-zone.models';
import { cloneDeep } from 'lodash';
import { AppConfig } from '../../app/app.config';
import { DateParserService } from '../daily-log/daily-log-time/date-parser-service';

@Injectable()
export class JackingActivityValidationService {

  config = AppConfig.settings;

  constructor(
    private http: HttpClient,
    private jackingActivityService: JackingActivityService,
    private vesselService: VesselService,
    private dateParserService: DateParserService
  ) {
  }

  public async ValidateWithAPI(editor: JackingActivityEditor): Promise<ValidationResult> {
    editor = await this.dateParserService.removeOffsetFromLuxonDates(editor);
    const url = `${this.config.apiUrl}api/jacking/validate`;
    return this.http.post<ValidationResult>(url, editor).toPromise();
  }

  // todo - check if needed
  public ValidateLockedDayWithAPI(editor: JackingActivityEditor, dailyLogDate: PtLuxonDate): Observable<ValidationResult> {
    const url = `${this.config.apiUrl}api/jacking/validatelockedday/${dailyLogDate.dateTime.toMillis()}`;
    return this.http.post<ValidationResult>(url, editor);
  }

  // Checks if there are outstanding jacking activities which need to be completed
  // before the day can be completed
  public async ValidateForDay(ptDate: PtLuxonDate): Promise<Boolean> {
    return this.jackingActivityService.getActivitiesCompleteForDay(ptDate).then(res => {
      return res.every(e => e.confirmed);
    });
  }

  public async CalculateMissingFields(
    editor: JackingActivityEditor
  ): Promise<ValidationResult> {
    const result = new ValidationResult();

    let vesselClassId = 0;
    await this.vesselService.getEditor().then(vessel => {
      vesselClassId = vessel.vesselClassId;
    });
    if (vesselClassId === 0) {
      throw new Error(
        `Unable to validate Jacking Activities for vessel class ${vesselClassId}`
      );
    }

    // Main Details
    if (
      (vesselClassId === VesselClasses.NG5500X && editor.comment === null) ||
      editor.comment === undefined
    ) {
      result.addError('Comments', ErrorType.Error);
    }

    // Project Info
    await Promise.all([
      this.CalcuatelMissingFields_ProjectInfo(editor, result, vesselClassId),
      this.CalculateMissingFields_LocationInfo(editor, result, vesselClassId),
      this.CalculateMissingFields_VesselInfo(editor, result, vesselClassId),
      this.CalculateMissingFields_ShockPadInfo(editor, result, vesselClassId),
      this.CalculateMissingFields_FinalJackingInfo(editor, result, vesselClassId),
      this.CalculateMissingFields_LegSummary(editor, result, vesselClassId),
      this.CalculateMissingFields_DeepWell(editor, result, vesselClassId),
      this.CalcualteEventsWithNullDates(editor, result)
    ]);

    return result;
  }

  private isPropertyNull(property: any): boolean {
    if (typeof property === 'number') {
      return property === null || property === undefined;
    } else if (!property) {
      return true;
    } else {
      return false;
    }
  }

  private async CalcualteEventsWithNullDates(
    editor: JackingActivityEditor,
    validationResult: ValidationResult
  ) {
    editor.events.forEach(evnt => {
      if (evnt.isLinkedToStep && !evnt.time.dateTime) {
        validationResult.addError(
          `Event '${
            evnt.description
          }' has no start/end date and will be removed from the daily log`,
          ErrorType.Error
        );
      }
    });
  }

  private async CalcuatelMissingFields_ProjectInfo(
    editor: JackingActivityEditor,
    validationResult: ValidationResult,
    vesselClassId: number
  ) {
    // Last Location
    if (
      this.isPropertyNull(editor.projectInfo.lastLocationId) &&
      editor.type.id === JackingActivityTypes.Arrival
    ) {
      validationResult.addError('Project Info, Last Location', ErrorType.Error);
    }

    // Next Location
    if (
      this.isPropertyNull(editor.projectInfo.nextLocationId) &&
      (editor.type.id === JackingActivityTypes.Departure &&
        (vesselClassId === VesselClasses.NG2500X || vesselClassId === VesselClasses.NG5500X))
    ) {
      validationResult.addError('Project Info, Next Location', ErrorType.Error);
    }

    // Distance Travelled
    if (
      this.isPropertyNull(editor.projectInfo.distanceTravelledToCurrentLocation) &&
      editor.type.id === JackingActivityTypes.Arrival
    ) {
      validationResult.addError(
        'Project Info, Distance Travelled To Current Location',
        ErrorType.Error
      );
    }

    // Soil Type
    if (
      this.isPropertyNull(editor.projectInfo.soilType) &&
      (editor.type.id === JackingActivityTypes.Arrival ||
        (editor.type.id === JackingActivityTypes.Departure &&
          (vesselClassId === VesselClasses.NG14000X || vesselClassId === VesselClasses.M)))
    ) {
      validationResult.addError('Project Info, Soil Type', ErrorType.Error);
    }

    // Lower Expetected Penetration
    if (
      this.isPropertyNull(editor.projectInfo.lowerExpectedPenetration) &&
      editor.type.id === JackingActivityTypes.Arrival
    ) {
      validationResult.addError('Project Info, Lower Expected Penetration', ErrorType.Error);
    }

    // Upper Expetected Penetration
    if (
      this.isPropertyNull(editor.projectInfo.upperExpectedPenetration) &&
      editor.type.id === JackingActivityTypes.Arrival
    ) {
      validationResult.addError('Project Info, Upper Expected Penetration', ErrorType.Error);
    }

    // Lower Arrival Penetration
    if (
      this.isPropertyNull(editor.projectInfo.lowerArrivalPenetration) &&
      editor.type.id === JackingActivityTypes.Departure &&
      (vesselClassId === VesselClasses.NG14000X || vesselClassId === VesselClasses.M)
    ) {
      validationResult.addError('Project Info, Lower Arrival Penetration', ErrorType.Error);
    }

    // Upper Arrival Penetration
    if (
      this.isPropertyNull(editor.projectInfo.upperArrivalPenetration) &&
      editor.type.id === JackingActivityTypes.Departure &&
      (vesselClassId === VesselClasses.NG14000X || vesselClassId === VesselClasses.M)
    ) {
      validationResult.addError('Project Info, Upper Arrival Penetration', ErrorType.Error);
    }

    // Hs
    if (
      this.isPropertyNull(editor.projectInfo.hs) &&
      (vesselClassId === VesselClasses.NG14000X || vesselClassId === VesselClasses.M)
    ) {
      validationResult.addError('Project Info, Hs', ErrorType.Error);
    }

    // Period
    if (
      this.isPropertyNull(editor.projectInfo.period) &&
      (vesselClassId === VesselClasses.NG14000X || vesselClassId === VesselClasses.M)
    ) {
      validationResult.addError('Project Info, Period', ErrorType.Error);
    }

    // PreLoad From COA
    if (
      this.isPropertyNull(editor.projectInfo.maxPreLoad) &&
      (editor.type.id === JackingActivityTypes.Arrival &&
        vesselClassId === VesselClasses.NG5500X)
    ) {
      validationResult.addError('Project Info, PreLoad from COA', ErrorType.Error);
    }

    // Last Air Gap
    if (
      this.isPropertyNull(editor.projectInfo.lastAirGap) &&
      (editor.type.id === JackingActivityTypes.Departure &&
        (vesselClassId === VesselClasses.NG5500X ||
          vesselClassId === VesselClasses.NG2500X))
    ) {
      validationResult.addError('Project Info, Last Air Gap', ErrorType.Error);
    }

    // Legs Free Time
    if (
      (this.isPropertyNull(editor.endDate.dateTime) || !editor.endTimeConfirmed) &&
      editor.type.id === JackingActivityTypes.Departure
    ) {
      validationResult.addError(
        'Project Info, Legs Free Time must be confirmed',
        ErrorType.Error
      );
    }
  }

  private async CalculateMissingFields_LocationInfo(
    editor: JackingActivityEditor,
    validationResult: ValidationResult,
    vesselClassId: number
  ) {
    // Range Of Tide
    if (this.isPropertyNull(editor.locationInfo.rangeOfTide)) {
      validationResult.addError('Location Data, Range Of Tide', ErrorType.Error);
    }

    // SurveyedCD
    if (
      this.isPropertyNull(editor.locationInfo.surveyedCD) &&
      (editor.type.id === JackingActivityTypes.Arrival ||
        (editor.type.id === JackingActivityTypes.Departure &&
          (vesselClassId === VesselClasses.NG14000X || vesselClassId === VesselClasses.M)))
    ) {
      validationResult.addError('Location Data, Surveyed CD', ErrorType.Error);
    }

    // Water Depth
    if (
      this.isPropertyNull(editor.locationInfo.waterDepth) &&
      (editor.type.id === JackingActivityTypes.Departure &&
        (vesselClassId === VesselClasses.NG2500X ||
          vesselClassId === VesselClasses.NG5500X))
    ) {
      validationResult.addError('Location Data, Water Depth', ErrorType.Error);
    }

    // Sea State
    if (this.isPropertyNull(editor.locationInfo.seaStateId)) {
      validationResult.addError('Location Data, Sea State', ErrorType.Error);
    }

    // Notes
    if (this.isPropertyNull(editor.locationInfo.note)) {
      validationResult.addError('Location Data, Notes', ErrorType.Error);
    }

    // HW1
    if (this.isPropertyNull(editor.locationInfo.hW1)) {
      validationResult.addError('Location Data, HW1', ErrorType.Error);
    }

    // HW2
    if (this.isPropertyNull(editor.locationInfo.hW2)) {
      validationResult.addError('Location Data, HW2', ErrorType.Error);
    }

    // LW1
    if (this.isPropertyNull(editor.locationInfo.lW1)) {
      validationResult.addError('Location Data, LW1', ErrorType.Error);
    }

    // LW2
    if (this.isPropertyNull(editor.locationInfo.lW2)) {
      validationResult.addError('Location Data, LW2', ErrorType.Error);
    }
  }

  private async CalculateMissingFields_VesselInfo(
    editor: JackingActivityEditor,
    validationResult: ValidationResult,
    vesselClassId: number
  ) {
    // Calc El. Wt
    if (this.isPropertyNull(editor.vesselSetup.calcElWt)) {
      validationResult.addError('Vessel Info, Calc El. Wt', ErrorType.Error);
    }

    // Draft
    if (this.isPropertyNull(editor.vesselSetup.draft)) {
      validationResult.addError('Vessel Info, Draft', ErrorType.Error);
    }

    // Trim
    if (this.isPropertyNull(editor.vesselSetup.trim)) {
      validationResult.addError('Vessel Info, Trim', ErrorType.Error);
    }

    // List
    if (this.isPropertyNull(editor.vesselSetup.list)) {
      validationResult.addError('Vessel Info, List', ErrorType.Error);
    }

    // LCG
    if (this.isPropertyNull(editor.vesselSetup.lcg)) {
      validationResult.addError('Vessel Info, LCG', ErrorType.Error);
    }

    // TCG
    if (this.isPropertyNull(editor.vesselSetup.tcg)) {
      validationResult.addError('Vessel Info, TCG', ErrorType.Error);
    }
  }

  private async CalculateMissingFields_ShockPadInfo(
    editor: JackingActivityEditor,
    validationResult: ValidationResult,
    vesselClassId: number
  ) {
    // Notes
    if (
      this.isPropertyNull(editor.shockPadInfo.notes) &&
      (vesselClassId === VesselClasses.NG14000X || vesselClassId === VesselClasses.M || vesselClassId === VesselClasses.NG2500X)
    ) {
      validationResult.addError('Shock Pads, Notes', ErrorType.Error);
    }

    // Other POS
    if (
      this.isPropertyNull(editor.shockPadInfo.otherPOS) &&
      vesselClassId === VesselClasses.NG2500X
    ) {
      validationResult.addError('Shock Pads, Other POS', ErrorType.Error);
    }

    // Other Posses
    if (
      this.isPropertyNull(editor.shockPadInfo.otherPosses) &&
      (vesselClassId === VesselClasses.NG14000X || vesselClassId === VesselClasses.M)
    ) {
      validationResult.addError('Shock Pads, Other Posses', ErrorType.Error);
    }

    // Existing Pen - Leg 1
    if (
      this.isPropertyNull(editor.shockPadInfo.leg1.existingPenetration) &&
      ((vesselClassId === VesselClasses.NG14000X || vesselClassId === VesselClasses.M) &&
        editor.type.id === JackingActivityTypes.Departure)
    ) {
      validationResult.addError(
        'Shock Pad, Leg 1 - Existing Penetration',
        ErrorType.Error
      );
    }

    // Existing Pen - Leg 2
    if (
      this.isPropertyNull(editor.shockPadInfo.leg2.existingPenetration) &&
      ((vesselClassId === VesselClasses.NG14000X || vesselClassId === VesselClasses.M) &&
        editor.type.id === JackingActivityTypes.Departure)
    ) {
      validationResult.addError(
        'Shock Pad, Leg 2 - Existing Penetration',
        ErrorType.Error
      );
    }

    // Existing Pen - Leg 3
    if (
      this.isPropertyNull(editor.shockPadInfo.leg3.existingPenetration) &&
      ((vesselClassId === VesselClasses.NG14000X || vesselClassId === VesselClasses.M) &&
        editor.type.id === JackingActivityTypes.Departure)
    ) {
      validationResult.addError(
        'Shock Pad, Leg 3 - Existing Penetration',
        ErrorType.Error
      );
    }

    // Existing Pen - Leg 4
    if (
      this.isPropertyNull(editor.shockPadInfo.leg4.existingPenetration) &&
      ((vesselClassId === VesselClasses.NG14000X || vesselClassId === VesselClasses.M) &&
        editor.type.id === JackingActivityTypes.Departure)
    ) {
      validationResult.addError(
        'Shock Pad, Leg 4 - Existing Penetration',
        ErrorType.Error
      );
    }
  }

  private async CalculateMissingFields_FinalJackingInfo(
    editor: JackingActivityEditor,
    validationResult: ValidationResult,
    vesselClassId: number
  ) {
    if (editor.type.id === JackingActivityTypes.Arrival) {
      // Final Leg Values Time
      if (this.isPropertyNull(editor.finalJackingInfo.finalLegValuesTime.dateTime)) {
        validationResult.addError(
          'Final Jacking Data, Final Leg Values Time is not recorded',
          ErrorType.Error
        );
      }
      // Leg 1 - Load
      if (this.isPropertyNull(editor.finalJackingInfo.leg1.load)) {
        validationResult.addError('Final Jacking Data, Leg 1 - Load', ErrorType.Error);
      }
      // Leg 2 - Load
      if (this.isPropertyNull(editor.finalJackingInfo.leg2.load)) {
        validationResult.addError('Final Jacking Data, Leg 2 - Load', ErrorType.Error);
      }
      // Leg 3 - Load
      if (this.isPropertyNull(editor.finalJackingInfo.leg3.load)) {
        validationResult.addError('Final Jacking Data, Leg 3 - Load', ErrorType.Error);
      }
      // Leg 4 - Load
      if (this.isPropertyNull(editor.finalJackingInfo.leg4.load)) {
        validationResult.addError('Final Jacking Data, Leg 4 - Load', ErrorType.Error);
      }
      // Leg 1 - Ext
      if (this.isPropertyNull(editor.finalJackingInfo.leg1.ext)) {
        validationResult.addError('Final Jacking Data, Leg 1 - Ext', ErrorType.Error);
      }
      // Leg 2 - Ext
      if (this.isPropertyNull(editor.finalJackingInfo.leg2.ext)) {
        validationResult.addError('Final Jacking Data, Leg 2 - Ext', ErrorType.Error);
      }
      // leg 3 - Ext
      if (this.isPropertyNull(editor.finalJackingInfo.leg3.ext)) {
        validationResult.addError('Final Jacking Data, Leg 3 - Ext', ErrorType.Error);
      }
      // Leg 4 - Ext
      if (this.isPropertyNull(editor.finalJackingInfo.leg4.ext)) {
        validationResult.addError('Final Jacking Data, Leg 4 - Ext', ErrorType.Error);
      }

      // Difference From Expected Time
      if (this.isPropertyNull(editor.finalJackingInfo.differenceFromExpectedTime.dateTime)) {
        validationResult.addError(
          'Final Jacking Data, Difference From Expected Time is not recorded',
          ErrorType.Error
        );
      }
      // Leg 1 - Difference From Expected
      if (this.isPropertyNull(editor.finalJackingInfo.leg1.differenceFromExpected)) {
        validationResult.addError(
          'Final Jacking Data, Leg 1 - Difference From Expected',
          ErrorType.Error
        );
      }
      // Leg 2 - Difference From Expected
      if (this.isPropertyNull(editor.finalJackingInfo.leg2.differenceFromExpected)) {
        validationResult.addError(
          'Final Jacking Data, Leg 2 - Difference From Expected',
          ErrorType.Error
        );
      }
      // Leg 3 - Difference From Expected
      if (this.isPropertyNull(editor.finalJackingInfo.leg3.differenceFromExpected)) {
        validationResult.addError(
          'Final Jacking Data, Leg 3 - Difference From Expected',
          ErrorType.Error
        );
      }
      // Leg 4 - Difference From Expected
      if (this.isPropertyNull(editor.finalJackingInfo.leg4.differenceFromExpected)) {
        validationResult.addError(
          'Final Jacking Data, Leg 4 - Difference From Expected',
          ErrorType.Error
        );
      }

      // Final Penetration Time
      if (this.isPropertyNull(editor.finalJackingInfo.finalPenetrationsTime.dateTime)) {
        validationResult.addError(
          'Final Jacking Data, Final Penetration Time is not recorded',
          ErrorType.Error
        );
      }
      // Leg 1 - Final Penetration
      if (this.isPropertyNull(editor.finalJackingInfo.leg1.finalPenetration)) {
        validationResult.addError(
          'Final Jacking Data, Leg 1 - Final Penetration',
          ErrorType.Error
        );
      }
      // Leg 2 - Final Penetration
      if (this.isPropertyNull(editor.finalJackingInfo.leg2.finalPenetration)) {
        validationResult.addError(
          'Final Jacking Data, Leg 2 - Final Penetration',
          ErrorType.Error
        );
      }
      // Leg 3 - Final Penetration
      if (this.isPropertyNull(editor.finalJackingInfo.leg3.finalPenetration)) {
        validationResult.addError(
          'Final Jacking Data, Leg 3 - Final Penetration',
          ErrorType.Error
        );
      }
      // Leg 4 - Final Penetration
      if (this.isPropertyNull(editor.finalJackingInfo.leg4.finalPenetration)) {
        validationResult.addError(
          'Final Jacking Data, Leg 4 - Final Penetration',
          ErrorType.Error
        );
      }

      // RPD
      if (vesselClassId === VesselClasses.NG14000X || vesselClassId === VesselClasses.M) {
        // Leg 1 - RPD
        if (this.isPropertyNull(editor.finalJackingInfo.leg1.r)) {
          validationResult.addError('Final Jacking Data, Leg 1 - R', ErrorType.Error);
        }

        if (this.isPropertyNull(editor.finalJackingInfo.leg1.p)) {
          validationResult.addError('Final Jacking Data, Leg 1 - P', ErrorType.Error);
        }

        if (this.isPropertyNull(editor.finalJackingInfo.leg1.d)) {
          validationResult.addError('Final Jacking Data, Leg 1 - D', ErrorType.Error);
        }

        // Leg 2 - RPD
        if (this.isPropertyNull(editor.finalJackingInfo.leg2.r)) {
          validationResult.addError('Final Jacking Data, Leg 2 - R', ErrorType.Error);
        }

        if (this.isPropertyNull(editor.finalJackingInfo.leg2.p)) {
          validationResult.addError('Final Jacking Data, Leg 2 - P', ErrorType.Error);
        }

        if (this.isPropertyNull(editor.finalJackingInfo.leg2.d)) {
          validationResult.addError('Final Jacking Data, Leg 2 - D', ErrorType.Error);
        }

        // Leg 3 - RPD
        if (this.isPropertyNull(editor.finalJackingInfo.leg3.r)) {
          validationResult.addError('Final Jacking Data, Leg 3 - R', ErrorType.Error);
        }

        if (this.isPropertyNull(editor.finalJackingInfo.leg3.p)) {
          validationResult.addError('Final Jacking Data, Leg 3 - P', ErrorType.Error);
        }

        if (this.isPropertyNull(editor.finalJackingInfo.leg3.d)) {
          validationResult.addError('Final Jacking Data, Leg 3 - D', ErrorType.Error);
        }

        // Leg 4 - RPD
        if (this.isPropertyNull(editor.finalJackingInfo.leg4.r)) {
          validationResult.addError('Final Jacking Data, Leg 4 - R', ErrorType.Error);
        }

        if (this.isPropertyNull(editor.finalJackingInfo.leg4.p)) {
          validationResult.addError('Final Jacking Data, Leg 4 - P', ErrorType.Error);
        }

        if (this.isPropertyNull(editor.finalJackingInfo.leg4.d)) {
          validationResult.addError('Final Jacking Data, Leg 4 - D', ErrorType.Error);
        }
      }

      // Deepwell Tie In Number & Depth
      if (vesselClassId === VesselClasses.NG2500X) {
        // Deep Well Tie In Number
        if (this.isPropertyNull(editor.finalJackingInfo.deepWellTieInNumberTime.dateTime)) {
          validationResult.addError(
            'Final Jacking Data, Deepwell Tie In Number Time is not recorded',
            ErrorType.Error
          );
        }
        // Deep Well Tie In Number - Leg 3
        if (this.isPropertyNull(editor.finalJackingInfo.leg3.deepWellTieInNumber)) {
          validationResult.addError(
            'Final Jacking Data, Leg 3 - Deep Well Tie In Number',
            ErrorType.Error
          );
        }
        // Deep Well Tie In Number - Leg 4
        if (this.isPropertyNull(editor.finalJackingInfo.leg4.deepWellTieInNumber)) {
          validationResult.addError(
            'Final Jacking Data, Leg 4 - Deep Well Tie In Number',
            ErrorType.Error
          );
        }

        // Deep Well Tie In Number
        if (this.isPropertyNull(editor.finalJackingInfo.deepWellTieInDepthTime.dateTime)) {
          validationResult.addError(
            'Final Jacking Data, Deepwell Tie In Depth Time is not recorded',
            ErrorType.Error
          );
        }
        // Deep Well Tie In Depth - Leg 3
        if (this.isPropertyNull(editor.finalJackingInfo.leg3.deepWellTieInDepth)) {
          validationResult.addError(
            'Final Jacking Data, Leg 3 - Deep Well Tie In Depth',
            ErrorType.Error
          );
        }
        // Deep Well Tie In Depth - Leg 4
        if (this.isPropertyNull(editor.finalJackingInfo.leg4.deepWellTieInDepth)) {
          validationResult.addError(
            'Final Jacking Data, Leg 4 - Deep Well Tie In Depth',
            ErrorType.Error
          );
        }
      }

      // Depth Deployed
      if (
        this.isPropertyNull(editor.finalJackingInfo.depthDeployed) &&
        (vesselClassId === VesselClasses.NG14000X || vesselClassId === VesselClasses.M)
      ) {
        validationResult.addError('Final Jacking Data, Depth Deployed', ErrorType.Error);
      }

      // Final Air Gap Time
      if (this.isPropertyNull(editor.finalJackingInfo.finalAirGapTime.dateTime)) {
        validationResult.addError(
          'Final Jacking Data, Final Air Gap Time is not recorded',
          ErrorType.Error
        );
      }
      if (!editor.finalJackingInfo.finalAirGapTimeConfirmed) {
        validationResult.addError(
          'Final Jacking Data, Final Air Gap Time is not confirmed',
          ErrorType.Error
        );
      }
      // Final Air Gap
      if (this.isPropertyNull(editor.finalJackingInfo.finalAirGap)) {
        validationResult.addError('Final Jacking Data, Final Air Gap', ErrorType.Error);
      }

      // Notes
      if (
        this.isPropertyNull(editor.finalJackingInfo.notes) &&
        ((vesselClassId === VesselClasses.NG14000X || vesselClassId === VesselClasses.M) ||
          vesselClassId === VesselClasses.NG5500X)
      ) {
        validationResult.addError('Final Jacking Data, Notes', ErrorType.Error);
      }

      // Davit Seajet Deployed To
      if (
        this.isPropertyNull(editor.finalJackingInfo.davitSeajetDeployedTo) &&
        vesselClassId === VesselClasses.NG2500X
      ) {
        validationResult.addError(
          'Final Jacking Data, Davit Seajet Deployed To',
          ErrorType.Error
        );
      }

      if (
        (vesselClassId === VesselClasses.NG14000X || vesselClassId === VesselClasses.M) ||
        vesselClassId === VesselClasses.NG2500X
      ) {
        // Northing - Leg 1
        if (this.isPropertyNull(editor.finalJackingInfo.leg1.northing)) {
          validationResult.addError(
            'Final Jacking Data, Leg 1 - Northing',
            ErrorType.Error
          );
        }

        // Northing - Leg 2
        if (this.isPropertyNull(editor.finalJackingInfo.leg2.northing)) {
          validationResult.addError(
            'Final Jacking Data, Leg 2 - Northing',
            ErrorType.Error
          );
        }

        // Northing - Leg 3
        if (this.isPropertyNull(editor.finalJackingInfo.leg3.northing)) {
          validationResult.addError(
            'Final Jacking Data, Leg 3 - Northing',
            ErrorType.Error
          );
        }
        // Northing - Leg 4
        if (this.isPropertyNull(editor.finalJackingInfo.leg4.northing)) {
          validationResult.addError(
            'Final Jacking Data, Leg 4 - Northing',
            ErrorType.Error
          );
        }

        // Northing - CRP
        if (this.isPropertyNull(editor.finalJackingInfo.northingCRP)) {
          validationResult.addError('Final Jacking Data, Northing CRP', ErrorType.Error);
        }

        // Easting - Leg 1
        if (this.isPropertyNull(editor.finalJackingInfo.leg1.easting)) {
          validationResult.addError(
            'Final Jacking Data, Leg 1 - Easting',
            ErrorType.Error
          );
        }

        // Easting - Leg 2
        if (this.isPropertyNull(editor.finalJackingInfo.leg2.easting)) {
          validationResult.addError(
            'Final Jacking Data, Leg 2 - Easting',
            ErrorType.Error
          );
        }

        // Easting - Leg 3
        if (this.isPropertyNull(editor.finalJackingInfo.leg3.easting)) {
          validationResult.addError(
            'Final Jacking Data, Leg 3 - Easting',
            ErrorType.Error
          );
        }

        // Easting - Leg 4
        if (this.isPropertyNull(editor.finalJackingInfo.leg4.easting)) {
          validationResult.addError(
            'Final Jacking Data, Leg 4 - Easting',
            ErrorType.Error
          );
        }

        // Easting - CRP
        if (this.isPropertyNull(editor.finalJackingInfo.eastingCRP)) {
          validationResult.addError('Final Jacking Data, Easting CRP', ErrorType.Error);
        }

        // Easting - Distance To Asset
        if (this.isPropertyNull(editor.finalJackingInfo.eastingDistanceToAsset)) {
          validationResult.addError(
            'Final Jacking Data, Easting Distance To Asset',
            ErrorType.Error
          );
        }
      }

      // Seajets Used Time
      if (
        (vesselClassId === VesselClasses.NG14000X || vesselClassId === VesselClasses.M) &&
        this.isPropertyNull(editor.finalJackingInfo.seajetsUsedTime.dateTime)
      ) {
        validationResult.addError(
          'Final Jacking Data, Seajets Used Time is not recorded',
          ErrorType.Error
        );
      }

      // Davit Pump in use time
      if (
        vesselClassId === VesselClasses.NG5500X &&
        this.isPropertyNull(editor.finalJackingInfo.davitPumpUsedTime.dateTime)
      ) {
        validationResult.addError(
          'Final Jacking Data, Davit Pump In Use Time is not recorded',
          ErrorType.Error
        );
      }
    }
  }

  private async CalculateMissingFields_LegSummary(
    editor: JackingActivityEditor,
    validationResult: ValidationResult,
    vesselClassId: number
  ) {
    if (editor.type.id === JackingActivityTypes.Departure) {
      // Pulling Max Load - Leg 1
      if (this.isPropertyNull(editor.legInfo.leg1.legMaxPullingLoad)) {
        validationResult.addError(
          'Leg Summary, Leg 1 - Max Pulling Load',
          ErrorType.Error
        );
      }

      // Pulling Max Load - Leg 2
      if (this.isPropertyNull(editor.legInfo.leg2.legMaxPullingLoad)) {
        validationResult.addError(
          'Leg Summary, Leg 2 - Max Pulling Load',
          ErrorType.Error
        );
      }

      // Pulling Max Load - Leg 3
      if (this.isPropertyNull(editor.legInfo.leg3.legMaxPullingLoad)) {
        validationResult.addError(
          'Leg Summary, Leg 3 - Max Pulling Load',
          ErrorType.Error
        );
      }

      // Pulling Max Load - Leg 4
      if (this.isPropertyNull(editor.legInfo.leg4.legMaxPullingLoad)) {
        validationResult.addError(
          'Leg Summary, Leg 4 - Max Pulling Load',
          ErrorType.Error
        );
      }

      // Pulling Max Load Time - Leg 1
      if (this.isPropertyNull(editor.legInfo.leg1.legMaxPullingloadTime)) {
        validationResult.addError(
          'Leg Summary, Leg 1 - Max Pulling Load Time',
          ErrorType.Error
        );
      }

      // Pulling Max Load Time - Leg 2
      if (this.isPropertyNull(editor.legInfo.leg2.legMaxPullingloadTime)) {
        validationResult.addError(
          'Leg Summary, Leg 2 - Max Pulling Load Time',
          ErrorType.Error
        );
      }

      // Pulling Max Load Time - Leg 3
      if (this.isPropertyNull(editor.legInfo.leg3.legMaxPullingloadTime)) {
        validationResult.addError(
          'Leg Summary, Leg 3 - Max Pulling Load Time',
          ErrorType.Error
        );
      }

      // Pulling Max Load Time - Leg 4
      if (this.isPropertyNull(editor.legInfo.leg4.legMaxPullingloadTime)) {
        validationResult.addError(
          'Leg Summary, Leg 4 - Max Pulling Load Time',
          ErrorType.Error
        );
      }

      // Free In Holes - Leg 1
      if (this.isPropertyNull(editor.legInfo.leg1.legFreeInHolesExt)) {
        validationResult.addError('Leg Summary, Leg 1 - Free In Holes', ErrorType.Error);
      }

      // Free In Holes - Leg 2
      if (this.isPropertyNull(editor.legInfo.leg2.legFreeInHolesExt)) {
        validationResult.addError('Leg Summary, Leg 2 - Free In Holes', ErrorType.Error);
      }

      // Free In Holes - Leg 3
      if (this.isPropertyNull(editor.legInfo.leg3.legFreeInHolesExt)) {
        validationResult.addError('Leg Summary, Leg 3 - Free In Holes', ErrorType.Error);
      }

      // Free In Holes - Leg 4
      if (this.isPropertyNull(editor.legInfo.leg4.legFreeInHolesExt)) {
        validationResult.addError('Leg Summary, Leg 4 - Free In Holes', ErrorType.Error);
      }

      // Free In Holes Time - Leg 1
      if (this.isPropertyNull(editor.legInfo.leg1.legFreeInHolesExtTime)) {
        validationResult.addError(
          'Leg Summary, Leg 1 - Free In Holes Time',
          ErrorType.Error
        );
      }

      // Free In Holes Time - Leg 2
      if (this.isPropertyNull(editor.legInfo.leg2.legFreeInHolesExtTime)) {
        validationResult.addError(
          'Leg Summary, Leg 2 - Free In Holes Time',
          ErrorType.Error
        );
      }

      // Free In Holes Time - Leg 3
      if (this.isPropertyNull(editor.legInfo.leg3.legFreeInHolesExtTime)) {
        validationResult.addError(
          'Leg Summary, Leg 3 - Free In Holes Time',
          ErrorType.Error
        );
      }

      // Free In Holes Time - Leg 4
      if (this.isPropertyNull(editor.legInfo.leg4.legFreeInHolesExtTime)) {
        validationResult.addError(
          'Leg Summary, Leg 4 - Free In Holes Time',
          ErrorType.Error
        );
      }

      // Clear Of Seabed - Leg 1
      if (this.isPropertyNull(editor.legInfo.leg1.clearOfSeaBedExt)) {
        validationResult.addError(
          'Leg Summary, Leg 1 - Clear Of Seabed',
          ErrorType.Error
        );
      }

      // Clear Of Seabed - Leg 2
      if (this.isPropertyNull(editor.legInfo.leg2.clearOfSeaBedExt)) {
        validationResult.addError(
          'Leg Summary, Leg 2 - Clear Of Seabed',
          ErrorType.Error
        );
      }

      // Clear Of Seabed - Leg 3
      if (this.isPropertyNull(editor.legInfo.leg3.clearOfSeaBedExt)) {
        validationResult.addError(
          'Leg Summary, Leg 3 - Clear Of Seabed',
          ErrorType.Error
        );
      }

      // Clear Of Seabed - Leg 4
      if (this.isPropertyNull(editor.legInfo.leg4.clearOfSeaBedExt)) {
        validationResult.addError(
          'Leg Summary, Leg 4 - Clear Of Seabed',
          ErrorType.Error
        );
      }

      // Clear Of Seabed Time - Leg 1
      if (this.isPropertyNull(editor.legInfo.leg1.clearOfSeaBedExtTime)) {
        validationResult.addError(
          'Leg Summary, Leg 1 - Clear Of Seabed Time',
          ErrorType.Error
        );
      }

      // Clear Of Seabed Time - Leg 2
      if (this.isPropertyNull(editor.legInfo.leg2.clearOfSeaBedExtTime)) {
        validationResult.addError(
          'Leg Summary, Leg 2 - Clear Of Seabed Time',
          ErrorType.Error
        );
      }

      // Clear Of Seabed Time - Leg 3
      if (this.isPropertyNull(editor.legInfo.leg3.clearOfSeaBedExtTime)) {
        validationResult.addError(
          'Leg Summary, Leg 3 - Clear Of Seabed Time',
          ErrorType.Error
        );
      }

      // Clear Of Seabed Time - Leg 4
      if (this.isPropertyNull(editor.legInfo.leg4.clearOfSeaBedExtTime)) {
        validationResult.addError(
          'Leg Summary, Leg 4 - Clear Of Seabed Time',
          ErrorType.Error
        );
      }
    }
  }

  private async CalculateMissingFields_DeepWell(
    editor: JackingActivityEditor,
    validationResult: ValidationResult,
    vesselClassId: number
  ) {
    if (
      vesselClassId === VesselClasses.NG2500X &&
      editor.type.id === JackingActivityTypes.Departure
    ) {
      // Leg 4 No
      if (this.isPropertyNull(editor.deepWellInfo.deepWellTieInLeg4)) {
        validationResult.addError('Deep Well, Leg 4 No.', ErrorType.Error);
      }

      // Leg 4 Lift at
      if (this.isPropertyNull(editor.deepWellInfo.deepWellTieInLeg4LiftAt)) {
        validationResult.addError('Deep Well, Leg 4 Lift At', ErrorType.Error);
      }

      // Leg 3 No
      if (this.isPropertyNull(editor.deepWellInfo.deepWellTieInLeg3)) {
        validationResult.addError('Deep Well, Leg 3 No.', ErrorType.Error);
      }

      // Leg 3 Lift at
      if (this.isPropertyNull(editor.deepWellInfo.deepWellTieInLeg3LiftAt)) {
        validationResult.addError('Deep Well, Leg 3 Lift At', ErrorType.Error);
      }
    }
  }
}



// WEBPACK FOOTER //
// ./src/services/jacking-activity/jacking-activity-validation.service.ts